export interface DetailedBullet {
  title: string
  detail: string
}

export interface Bonus {
  id: string
  title: string
  subtitle: string
  intro?: string
  bulletsIntro?: string
  bullets?: string[]
  detailedBullets?: DetailedBullet[]
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  systemLabel?: string
  system?: string
  result?: string
  closing?: string[]
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: 'Heavy Equipment Instructor Lesson Planning System',
    subtitle: 'Build Structured Machinery Training Sessions Faster',
    intro:
      'A complete professional planning resource created to help instructors prepare structured lessons without starting from zero.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Lesson planning frameworks',
      'Learning objectives',
      'Classroom preparation',
      'Teaching sequences',
      'Equipment-specific lesson planning',
      'Instructor checklists',
      'Student briefing templates',
      'Training session organization',
      'Discussion activities',
      'Knowledge checks',
      'Assessment planning',
      'Reusable lesson templates',
    ],
    secondaryBulletsIntro: 'Ready-to-Adapt Training Formats',
    secondaryBullets: ['30-MINUTE LESSONS', '45-MINUTE LESSONS', '60-MINUTE LESSONS', '90-MINUTE WORKSHOPS'],
    result: 'Spend less time creating lesson structures and more time delivering professional training.',
  },
  {
    id: '02',
    title: 'Heavy Equipment Scenario & Decision-Making Library',
    subtitle: 'Ready-to-Use Workplace Scenarios for Instructor-Led Training',
    intro:
      'A professional written scenario library designed to help students analyze workplace situations, identify hazards and develop better technical understanding.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Forklift scenarios',
      'Warehouse traffic',
      'Pedestrian interactions',
      'Load-related hazards',
      'Excavation environments',
      'Ground conditions',
      'Underground utility awareness',
      'Loader and bulldozer environments',
      'Machinery visibility',
      'Crane worksite scenarios',
      'Lifting equipment limitations',
      'Aerial platform situations',
      'Elevated-workplace hazards',
      'Road construction scenarios',
      'Worksite communication',
      'Hazard recognition',
      'Instructor discussion questions',
      'Scenario updates',
      'Reference answers',
      'Debriefing guidance',
    ],
    result:
      'Add realistic variety and meaningful classroom discussions without developing every scenario independently.',
  },
  {
    id: '03',
    title: 'Heavy Equipment Assessment & Progress Tracking Handbook',
    subtitle: 'Evaluate Knowledge, Safety Awareness & Training Development',
    intro:
      'A professional instructor resource designed to organize student assessments and monitor classroom learning.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Equipment knowledge assessments',
      'Technical understanding reviews',
      'Hazard-recognition evaluations',
      'Instructor observation records',
      'Scenario assessments',
      'Student progress documentation',
      'Common-error tracking',
      'Knowledge gap identification',
      'Training feedback',
      'Improvement planning',
      'Reassessment records',
      'Evaluation templates',
    ],
    secondaryBulletsIntro: 'Four Learning Levels',
    secondaryBullets: ['NEEDS GUIDANCE', 'DEVELOPING', 'CONSISTENT', 'STRONG'],
    result: 'Evaluate learning more consistently and make future training objectives easier to identify.',
  },
  {
    id: '04',
    title: 'Heavy Equipment Instructor Question Bank',
    subtitle: '360 Questions for Knowledge Checks, Discussions & Training Reviews',
    intro: 'A complete written question library covering the six specialized machinery categories.',
    bulletsIntro: 'Includes:',
    detailedBullets: [
      {
        title: '60 FORKLIFT QUESTIONS',
        detail: 'Equipment anatomy, stability concepts, material handling, warehouse environments and hazards.',
      },
      {
        title: '60 EXCAVATOR QUESTIONS',
        detail: 'Excavator anatomy, hydraulic components, attachments, ground conditions and workplace safety.',
      },
      {
        title: '60 BULLDOZER, LOADER & GRADER QUESTIONS',
        detail: 'Machinery differences, components, terrain, visibility and construction environments.',
      },
      {
        title: '60 CRANE & LIFTING EQUIPMENT QUESTIONS',
        detail: 'Lifting structures, equipment components, documentation, rigging awareness and site hazards.',
      },
      {
        title: '60 AERIAL PLATFORM QUESTIONS',
        detail:
          'Scissor lifts, boom lifts, platform structures, equipment limitations and elevated-workplace awareness.',
      },
      {
        title: '60 ROAD EQUIPMENT QUESTIONS',
        detail: 'Rollers, compactors, asphalt pavers, construction-site organization and hazards.',
      },
    ],
    secondaryBulletsIntro: 'Organized Into',
    secondaryBullets: ['FOUNDATION', 'TECHNICAL UNDERSTANDING', 'HAZARD RECOGNITION', 'APPLIED SCENARIOS'],
    result:
      'Have a large collection of instructor questions available for classroom discussions, technical reviews and knowledge assessments.',
  },
  {
    id: '05',
    title: 'Heavy Equipment Training Debriefing & After-Action Review Manual',
    subtitle: 'Turn Every Training Session Into Better Future Performance',
    intro: 'A professional instructor resource designed to make training reviews more organized and effective.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Quick training debriefs',
      'Structured after-action reviews',
      'Student feedback methods',
      'Group learning discussions',
      'Equipment-specific review questions',
      'Hazard recognition analysis',
      'Decision-making discussions',
      'Instructor observation summaries',
      'Common-error reviews',
      'Knowledge gaps',
      'Student reflection',
      'Improvement plans',
      'Training documentation',
      'Follow-up objectives',
      'Debriefing templates',
    ],
    result: 'Turn training observations into useful feedback and clearer objectives for future sessions.',
  },
]
